import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock } from "lucide-react";
import { get, errMsg } from "@/lib/api";
import { Skel } from "@/components/app/Common";
import { AssessIntro } from "@/pages/assess/AssessIntro";
import { AssessItems } from "@/pages/assess/AssessItems";
import { AssessSjt } from "@/pages/assess/AssessSjt";
import { AssessExtra } from "@/pages/assess/AssessExtra";

export function AssessFrame({ info, children }) {
  return (
    <div className="min-h-screen bg-canvas" data-testid="assess-page">
      <header className="border-b border-line bg-white">
        <div className="mx-auto flex max-w-[680px] items-center gap-3 px-5 py-4">
          {info?.logo_data_url && <img src={info.logo_data_url} alt="" className="h-8 w-8 rounded object-contain" />}
          <span className="font-display text-lg font-semibold" style={{ color: info?.primary_color || "#1F4D3A" }} data-testid="assess-company">{info?.company_name || "Questionnaire"}</span>
        </div>
      </header>
      <main className="mx-auto max-w-[680px] space-y-5 px-4 py-6 sm:px-5 sm:py-10 tl-page">{children}</main>
    </div>
  );
}

const Message = ({ icon: Icon, title, text, testId, color }) => (
  <div className="panel space-y-3 p-6 sm:p-8" data-testid={testId}>
    <Icon size={30} style={{ color }} className="text-brand" />
    <h1 className="text-2xl font-semibold">{title}</h1>
    <p className="leading-relaxed text-ink-secondary">{text}</p>
  </div>
);

export const AssessDone = ({ info }) => (
  <Message icon={CheckCircle2} testId="assess-done" color={info?.primary_color} title="Thank you — you're all done"
    text={`Your answers for ${info?.job_title || "this role"} have been saved. The hiring team at ${info?.company_name || "the company"} will review them together with your application. There are no right or wrong answers, and a person makes every decision.`} />
);

export default function AssessPage() {
  const { token } = useParams();
  const [step, setStep] = useState(null);
  const { data: info, isLoading, error, refetch } = useQuery({ queryKey: ["assess", token], queryFn: () => get(`/public/assess/${token}`), retry: false, staleTime: Infinity });

  if (isLoading) return <AssessFrame><Skel className="h-72" /></AssessFrame>;
  if (error) {
    const code = error.response?.status;
    if (code === 409) return <AssessFrame><AssessDone /></AssessFrame>;
    return <AssessFrame><Message icon={Clock} testId={code === 410 ? "assess-expired" : "assess-invalid"} title={code === 410 ? "This link has expired" : "This link isn't valid"} text={errMsg(error)} /></AssessFrame>;
  }
  if (info.status === "completed" || step === "done") return <AssessFrame info={info}><AssessDone info={info} /></AssessFrame>;
  const current = step || (info.consented ? "items" : "intro");
  return (
    <AssessFrame info={info}>
      {info.sandbox && <p className="rounded-[6px] border border-[#E8D7B0] bg-caution-tint px-4 py-2 text-sm" role="status" data-testid="assess-sandbox-banner">Test run. Your answers are marked as a test and are not linked to any candidate. This link stops working 24 hours after it was created.</p>}
      {current === "intro" && <AssessIntro token={token} info={info} onStart={() => { refetch(); setStep("items"); }} />}
      {current === "items" && <AssessItems token={token} info={info} onFinished={setStep} />}
      {current === "sjt" && <AssessSjt token={token} info={info} onDone={setStep} />}
      {current === "extra-pre" && <AssessExtra token={token} info={info} part="pre" onDone={setStep} />}
      {current === "extra" && <AssessExtra token={token} info={info} part="post" onDone={() => setStep("done")} />}
    </AssessFrame>
  );
}
